import { Zap } from "lucide-react";
import { useLocale } from "@/hooks/use-locale";
import { curatedUiTranslation } from "@/lib/curated-ui-translations";

/**
 * Reel-to-web landing zone: three scannable takeaways directly under the
 * vertical video, so a viewer arriving from a 30-second reel gets the payoff
 * before deciding to read. Derived from the article body - no extra cost.
 */
export function FastFacts({ body, excerpt }: { body: string; excerpt?: string | null }) {
  const [lang] = useLocale();
  const paragraphs = body
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p.length > 60);

  if (paragraphs.length < 3) return null;

  const firstSentence = (p: string) => {
    const s = p.split(/(?<=[.!?])\s+/)[0] ?? p;
    return s.length > 150 ? `${s.slice(0, 147).trimEnd()}…` : s;
  };

  const picks = [
    excerpt?.trim() || firstSentence(paragraphs[0]!),
    firstSentence(paragraphs[Math.floor(paragraphs.length / 2)]!),
    firstSentence(paragraphs[paragraphs.length - 1]!),
  ];

  return (
    <section
      aria-label={curatedUiTranslation(lang, "The short version") ?? "Fast facts"}
      className="mt-4 rounded-2xl border border-border bg-card/50 p-4 md:mt-6 md:p-5"
    >
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
        <Zap className="h-3.5 w-3.5" /> {curatedUiTranslation(lang, "The short version") ?? "The short version"}
      </div>
      <ul className="mt-3 grid gap-2.5">
        {picks.map((p, i) => (
          <li key={i} className="flex gap-3 text-[15px] leading-snug text-foreground md:text-base">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
            <span className="min-w-0">{p}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
